import React, { createContext, useContext, useMemo } from 'react';
import { studioFormatters, FALLBACK_LOCALE } from '../domain/locale';

// Where the studio's one locale setting reaches the screen.
//
// MMS has no currency setting: a Region dropdown in Edit Studio Profile decides
// currency, mileage units and date format together. src/domain/locale.js is
// the derivation; this is the wiring, so that a component asks for "how do I
// draw money here" rather than importing a formatter and getting USD.
//
// This provider reads no Firestore and holds no state. The locale arrives as a
// prop — `LocaleBridge` in src/App.jsx passes `settings.locale` from
// src/data/useSettings.jsx — and an undefined locale falls back to
// FALLBACK_LOCALE.

const LocaleContext = createContext(null);

// The derivation, with the locale already tied into the formatters: one place,
// so the provider and the no-provider fallback below cannot bind different
// things. It is `studioFormatters` in the domain (#500), because the emails and
// texts a studio sends bind the same formatters to the same stored locale.
const bind = studioFormatters;

export function LocaleProvider({ locale, children }) {
  // Derived, not stored: with one input, currency and date format cannot drift
  // apart.
  const value = useMemo(() => bind(locale), [locale]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

// Outside a provider — a test rendering one component, a page mounted before
// the settings load — this is FALLBACK_LOCALE rather than a throw.
//
// Bound on first use, not at module scope: the derivation asks the Rust core,
// and src/main.jsx imports this file before `loadCore()` has resolved.
let fallback = null;

export function useLocale() {
  const value = useContext(LocaleContext);
  if (value) return value;
  fallback ??= bind(FALLBACK_LOCALE);
  return fallback;
}

// Named accessors, so a call site reads as `formatMoney(minor)`. They all read
// the same context and re-render together.
export function useMoneyFormat() {
  return useLocale().formatMoney;
}

// `formatMoney` with a negative in parentheses, `($10.00)`, as MMS writes a
// discount or a refund on the family ledger (#649).
export function useMoneyInParenthesesFormat() {
  return useLocale().formatMoneyInParentheses;
}

export function useDateFormat() {
  return useLocale().formatDate;
}

// The clock reading for a stored "HH:MM", in the studio's locale.
export function useTimeFormat() {
  return useLocale().formatTime;
}

export function useMonthFormat() {
  return useLocale().formatMonth;
}

// The day the calendar's week begins on, from the studio's Region (#505):
// date-fns' `weekStartsOn`, 0 Sunday .. 6 Saturday.
export function useWeekStartsOn() {
  return useLocale().weekStartsOn;
}

// The studio's short-date pattern (`shortDatePattern`), for a sentence the
// core builds around a date: the core draws the date into it, as
// `formatDate` does here (#510).
export function useDatePattern() {
  return useLocale().datePattern;
}

// For the other half of money: fromDecimal() and toDecimal() take a currency
// too. "3000" typed into a rate field is 3000 minor units in yen and 300000 in
// dollars, so a form must parse and print with the same currency.
export function useCurrency() {
  return useLocale().currency;
}
